import { callAdminApi } from './wixAdminApi';
import { Resource, BookingItem } from '../../types/bookings';

export class ResourcesApi {
  
  /**
   * Get all resource types
   */
  async getAllResourceTypes(): Promise<{ _id: string; name: string; [key: string]: unknown }[]> {
    try {
      const result = await callAdminApi('queryResourceTypes', 'resources', {});
      const items = (result as { _items?: { _id: string; name: string; [key: string]: unknown }[] })?._items || [];
      return items;
    } catch (error) {
      console.error('ResourcesApi.getAllResourceTypes error:', error);
      return [];
    }
  }

  /**
   * Create a resource category (Wix "ResourceType") — the same grouping
   * Wix's own dashboard calls a "category" (e.g. "Team Member 01").
   */
  async createResourceType(name: string): Promise<{ _id: string; name: string; revision?: string }> {
    const result = await callAdminApi('createResourceType', 'resources', { name });
    return result as { _id: string; name: string; revision?: string };
  }

  /**
   * Rename a resource category. `revision` is Wix's optimistic-concurrency
   * token — pass the value from the category record you last fetched.
   */
  async updateResourceType(resourceTypeId: string, name: string, revision?: string): Promise<void> {
    await callAdminApi('updateResourceType', 'resources', { resourceTypeId, name, revision });
  }

  /**
   * Delete a resource category. Resources that were in it become
   * uncategorized in Wix, not deleted.
   */
  async deleteResourceType(resourceTypeId: string): Promise<void> {
    await callAdminApi('deleteResourceType', 'resources', { resourceTypeId });
  }

  /**
   * Get all resources
   */
  async getAllResources(): Promise<Resource[]> {
    try {
      const result = await callAdminApi('queryResources', 'resources', {});
      const items = (result as { _items?: Resource[] })?._items || [];
      return items;
    } catch {
      return [];
    }
  }

  /**
   * Get resource by ID
   */
  async getResourceById(id: string): Promise<Resource | null> {
    try {
      const result = await callAdminApi('getResource', 'resources', { id });
      return result as Resource;
    } catch (error) {
      console.error('Error getting resource by ID:', error);
      return null;
    }
  }

  /**
   * Query resources with filters
   */
  async queryResources(params: {
    filter?: Partial<Resource> & {
      [key: string]: string | number | boolean | string[] | { $contains?: string; $eq?: string; $ne?: string; $in?: string[] };
    };
    limit?: number;
    skip?: number;
    sort?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  }): Promise<{ items: Resource[]; totalCount?: number }> {
    try {
      const result = await callAdminApi('queryResources', 'resources', params);
      const response = result as { _items?: Resource[]; _totalCount?: number };
      return { 
        items: response._items || [], 
        totalCount: response._totalCount 
      };
    } catch (error) {
      console.error('Error querying resources:', error);
      return { items: [] };
    }
  }

  /**
   * Create a new resource
   */
  async createResource(resourceData: Partial<Resource>): Promise<Resource> {
    try {
      const result = await callAdminApi('createResource', 'resources', resourceData);
      return result as Resource;
    } catch (error) {
      console.error('Error creating resource:', error);
      throw new Error('Failed to create resource');
    }
  }

  /**
   * Update a resource
   */
  async updateResource(id: string, resourceData: Partial<Resource>): Promise<Resource> {
    try {
      const result = await callAdminApi('updateResource', 'resources', { id, ...resourceData });
      return result as Resource;
    } catch (error) {
      console.error('Error updating resource:', error);
      throw new Error('Failed to update resource');
    }
  }

  /**
   * Delete a resource
   */
  async deleteResource(id: string): Promise<boolean> {
    try {
      await callAdminApi('deleteResource', 'resources', { id });
      return true;
    } catch (error) {
      console.error('Error deleting resource:', error);
      return false;
    }
  }

  /**
   * Get resource availability
   */
  async getResourceAvailability(resourceId: string, startDate: Date, endDate: Date): Promise<BookingItem[]> {
    try {
      const result = await callAdminApi('getResourceAvailability', 'resources', {
        resourceId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      return (result as { _items?: BookingItem[] })?._items || [];
    } catch (error) {
      console.error('Error getting resource availability:', error);
      return [];
    }
  }

  /**
   * Search resources by name or other criteria
   */
  async searchResources(query: string): Promise<Resource[]> {
    try {
      const result = await callAdminApi('queryResources', 'resources', {
        filter: {
          name: { $contains: query }
        }
      });
      return (result as { _items?: Resource[] })?._items || [];
    } catch (error) {
      console.error('Error searching resources:', error);
      return [];
    }
  }

  /**
   * List business locations, for the resource Location dropdown. Mirrors
   * Wix's own "Create Resource" dialog — a simple single-select, not a
   * multi-location picker.
   */
  async getBusinessLocations(): Promise<{ _id: string; name: string }[]> {
    try {
      const result = await callAdminApi('listBusinessLocations', 'resources', {});
      return (result as { locations?: { _id: string; name: string }[] })?.locations || [];
    } catch (error) {
      console.error('ResourcesApi.getBusinessLocations error:', error);
      return [];
    }
  }

  /**
   * Get resources grouped by category/team name
   */
  async getResourcesGroupedByCategory(): Promise<{ [category: string]: Resource[] }> {
    try {
      // Get both resource types and resources in parallel
      const [allResourceTypes, allResources] = await Promise.all([
        this.getAllResourceTypes(),
        this.getAllResources()
      ]);

      // Create a map of resource type ID to resource type name
      const resourceTypeMap = new Map<string, string>();
      allResourceTypes.forEach((resourceType: { _id: string; name: string; [key: string]: unknown }) => {
        resourceTypeMap.set(resourceType._id, resourceType.name);
      });

      const grouped: { [category: string]: Resource[] } = {};

      allResources.forEach((resource: Resource) => {
        let categoryName = 'Unassigned';

        // The real Wix Resource object links to its category via `typeId`,
        // not `type` — this was matching against a field that's always
        // undefined on live data, so every resource fell into "Unassigned".
        if (resource.typeId && resourceTypeMap.has(resource.typeId)) {
          categoryName = resourceTypeMap.get(resource.typeId)!;
        }

        if (!grouped[categoryName]) {
          grouped[categoryName] = [];
        }
        grouped[categoryName].push(resource);
      });

      return grouped;
    } catch {
      return {};
    }
  }
}
